import { useState } from 'react';
import {
  LockClosedIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  useCreateNote,
  useDeleteNote,
  useMemberNotes,
  useUpdateNote,
  type Note,
} from '@/hooks/useNotes';

function formatNoteDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function NoteRow({ note }: { note: Note }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(note.noteText);
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();

  const handleSave = async () => {
    const next = editText.trim();
    if (!next) return;
    if (next === note.noteText) {
      setIsEditing(false);
      return;
    }
    await updateNote.mutateAsync({ noteId: note.id, input: { noteText: next } });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    await deleteNote.mutateAsync(note.id);
  };

  return (
    <article className="rounded-xl border border-flock-gray-200 bg-flock-navy-50/40 p-3">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-flock-navy-900">{note.author.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-flock-navy-500">
            <span>{formatNoteDate(note.createdAt)}</span>
            {note.editedAt && <span className="italic">Edited</span>}
            {note.isPrivate ? (
              <span className="inline-flex items-center gap-1 font-semibold text-flock-navy-800">
                <LockClosedIcon className="h-3.5 w-3.5" />
                Private
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 font-semibold text-flock-navy-800">
                <UserGroupIcon className="h-3.5 w-3.5" />
                Leaders only
              </span>
            )}
          </div>
        </div>
        {note.isOwnNote && !isEditing && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setEditText(note.noteText);
                setIsEditing(true);
              }}
              className="min-h-11 rounded-lg px-2 text-xs font-medium text-flock-navy-600 hover:bg-white"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="min-h-11 rounded-lg px-2 text-xs font-medium text-red-600 hover:bg-white"
            >
              Delete
            </button>
          </div>
        )}
      </div>
      {isEditing ? (
        <div>
          <textarea
            value={editText}
            onChange={(event) => setEditText(event.target.value)}
            rows={3}
            className="w-full rounded-lg border border-flock-navy-800 bg-white p-3 text-sm text-flock-navy-900"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="min-h-11 rounded-lg border-2 border-flock-navy-600 px-3 text-sm font-semibold text-flock-navy-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={updateNote.isPending || !editText.trim()}
              className="min-h-11 rounded-lg bg-flock-navy-900 px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {updateNote.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm text-flock-navy-800">{note.noteText}</p>
      )}
    </article>
  );
}

export default function MemberNotesSection({
  userId,
  groupId,
}: {
  userId: string;
  groupId: string;
}) {
  const [noteText, setNoteText] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const notesQuery = useMemberNotes(userId, groupId);
  const createNote = useCreateNote();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = noteText.trim();
    if (!text) return;
    await createNote.mutateAsync({
      userId,
      input: {
        groupId,
        noteText: text,
        isPrivate,
      },
    });
    setNoteText('');
    setIsPrivate(true);
  };

  return (
    <section className="rounded-2xl border border-flock-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold text-flock-navy-900">Notes</h2>

      <form onSubmit={handleSubmit} className="mb-4 space-y-3">
        <textarea
          value={noteText}
          onChange={(event) => setNoteText(event.target.value)}
          rows={4}
          maxLength={5000}
          placeholder="Write a note about this member…"
          className="w-full rounded-xl border border-flock-gray-200 bg-flock-navy-50 p-3 text-sm text-flock-navy-900"
        />

        <div className="rounded-xl border-2 border-flock-navy-900 p-3">
          <label className="flex min-h-11 cursor-pointer items-center justify-between gap-3">
            <span>
              <span className="block text-sm font-semibold text-flock-navy-900">
                Private Note
              </span>
              <span className="block text-xs text-flock-navy-500">
                {isPrivate ? 'Only you will see this' : 'Off — other leaders can see this'}
              </span>
            </span>
            <span className="inline-flex min-h-11 min-w-14 items-center justify-center rounded-full border-2 border-flock-navy-900 px-1">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(event) => setIsPrivate(event.target.checked)}
                className="h-5 w-5 accent-flock-navy-800"
              />
            </span>
          </label>
          <div className="mt-3 flex items-center gap-2 border-t border-flock-navy-600 pt-3 text-sm font-semibold text-flock-navy-900">
            {isPrivate ? (
              <>
                <LockClosedIcon className="h-4 w-4" />
                Private — only you will see this
              </>
            ) : (
              <>
                <UserGroupIcon className="h-4 w-4" />
                Leaders only — other leaders can see this
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={createNote.isPending || !noteText.trim()}
            className="min-h-11 rounded-xl bg-flock-navy-900 px-5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {createNote.isPending ? 'Saving…' : 'Save note'}
          </button>
        </div>
        {createNote.isError && (
          <p className="text-sm text-red-600">Could not save this note. Please try again.</p>
        )}
      </form>

      {notesQuery.isLoading && (
        <p className="text-sm text-flock-navy-400">Loading notes…</p>
      )}
      {notesQuery.isError && (
        <p className="text-sm text-red-600">Could not load notes.</p>
      )}
      {!notesQuery.isLoading && (notesQuery.data?.notes.length ?? 0) === 0 && (
        <p className="text-sm text-flock-navy-400">No notes yet.</p>
      )}
      <div className="space-y-3">
        {notesQuery.data?.notes.map((note) => (
          <NoteRow key={note.id} note={note} />
        ))}
      </div>
    </section>
  );
}
